/**
 * @file MainComponent.tsx
 *
 * @description Main layout component that manages the application's sidebar and content area.
 * This component is used as a wrapper around the main content of the application and
 * provides the appropriate spacing and layout adjustments based on the sidebar state.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 */

import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useContext, useEffect, useState } from "react";
import { BodycamContext } from "../../context/BodycamContext";
import Sidebar from "./SideBar";
import TopAppBar from "./TopAppBar";
import { JobMonitorView } from "../jobs/JobMonitor";
import { ToastOffsetSync, useToast } from "../../shared/toast";
import { useJobMonitorState } from "../jobs/hooks";
import { ReportIssueWidget } from "../feedback/ReportIssueWidget";
import { MaintenanceBanner } from "../feedback/MaintenanceBanner";
import { useNewsModal } from "../../shared/hooks/useNewsModal";
import { NewsModal } from "../../modals/NewsModal";
import { profileClient, rmsClient } from "../../api/clients";
import { ProfileResponse } from "../../shared/interfaces/ProfileResponse";
import { useAuthContext } from "../../context/AuthContext";
// import { ChatPanelContainer } from "../chat/ChatPanelContainer";

// Width in pixels of the sidebar when fully expanded (by breakpoint)
const drawerWidth = 240;
const drawerWidthXXL = 300; // 2K displays
const drawerWidthUHD = 360; // 4K displays
// Width in pixels of the sidebar when collapsed/minimized (by breakpoint)
const closedDrawerWidth = 72;
const closedDrawerWidthXXL = 125; // 2K displays
const closedDrawerWidthUHD = 150; // 4K displays
// Height of the AppBar (standard MUI heights)
const appBarHeightDesktop = 64;
const appBarHeightMobile = 56;

const getDesktopLayoutStyles = (theme: Theme, open?: boolean) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: open
      ? theme.transitions.easing.easeOut
      : theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
  paddingTop: `calc(${appBarHeightDesktop}px + ${theme.spacing(2)})`,
});

/**
 * @name Main
 *
 * @description Styled component for the main content area that adjusts based on sidebar state.
 * Handles responsive behavior for different screen sizes and sidebar states.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.open] - Whether the sidebar is open or closed
 */
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0, // Prevent flex item from overflowing
  paddingBottom: theme.spacing(2),
  // 4K/UHD displays (3840px+)
  [theme.breakpoints.up("uhd")]: {
    marginLeft: open ? drawerWidthUHD : closedDrawerWidthUHD,
    width: `calc(100% - ${open ? drawerWidthUHD : closedDrawerWidthUHD}px)`,
    ...getDesktopLayoutStyles(theme, open),
  },
  // 2K/QHD displays (2560px - 3839px)
  [theme.breakpoints.between("xxl", "uhd")]: {
    marginLeft: open ? drawerWidthXXL : closedDrawerWidthXXL,
    width: `calc(100% - ${open ? drawerWidthXXL : closedDrawerWidthXXL}px)`,
    ...getDesktopLayoutStyles(theme, open),
  },
  // Standard large displays (1536px - 2559px)
  [theme.breakpoints.between("xl", "xxl")]: {
    marginLeft: open ? drawerWidth : closedDrawerWidth,
    width: `calc(100% - ${open ? drawerWidth : closedDrawerWidth}px)`,
    ...getDesktopLayoutStyles(theme, open),
  },
  [theme.breakpoints.between("lg", "xl")]: {
    marginLeft: open ? drawerWidth : closedDrawerWidth,
    width: `calc(100% - ${open ? drawerWidth : closedDrawerWidth}px)`,
    ...getDesktopLayoutStyles(theme, open),
  },
  [theme.breakpoints.down("lg")]: {
    marginLeft: 0,
    width: "100%",
    paddingTop: `calc(${appBarHeightMobile}px + ${theme.spacing(2)})`,
  },
}));

/**
 * @name MainComponent
 *
 * @description The main layout component that renders the sidebar and wraps the application content.
 * Handles sidebar toggle functionality and initial user profile data fetching.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render within the main content area
 *
 * @returns {JSX.Element} The rendered main component with sidebar and content
 */
export const MainComponent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  // const isEditSectionPage = location.pathname.startsWith("/edit-section");
  const { openSidebar, userInfo, customReports } = useContext(BodycamContext);
  const [open, setOpen] = openSidebar;
  const [userProfile, setUserProfile] = userInfo;
  const [customReportTypes, setCustomReportTypes] = customReports;
  const { organizationId } = useAuthContext();

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Shared job monitor state (used by AppBar + drawer)
  const jobMonitorState = useJobMonitorState();

  /**
   * @name handleSideBarClick
   *
   * @description Toggles the sidebar between open and closed states
   */
  const handleSideBarClick = () => {
    setOpen((prev) => !prev);
  };

  /**
   * @name handleMobileDrawerToggle
   *
   * @description Toggles the mobile drawer open/closed state
   */
  const handleMobileDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  /**
   * @name handleMobileDrawerClose
   *
   * @description Closes the mobile drawer
   */
  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  const toast = useToast();
  const {
    newsItems,
    maintenanceItems,
    open: isNewsModalOpen,
    fetchNews,
    handleClose: handleCloseNewsModal,
    dismissMaintenanceItem,
  } = useNewsModal();

  /**
   * Effect hook to fetch user profile information and
   * other station information when component mounts
   * or when userProfile is reset to null
   */
  useEffect(() => {
    if (!userProfile) {
      // Fetch user profile data from the API if not already available
      profileClient
        .get("/v1/profile")
        .then((response) => {
          const { user, memberships } = response.data as ProfileResponse;

          setUserProfile({
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            department: user.metadata.department,
            rank: user.metadata.position,
            badgeNumber: user.metadata.badge_number,
            stationName: memberships[0].organizationName,
            status: memberships[0].status,
          });
        })
        .catch((err) => {
          console.error(err);
          toast.error(
            "Failed to fetch user profile information. Please try again later.",
          );
        });
    }
    if (userProfile && !customReportTypes) {
      if (!customReportTypes) {
        rmsClient
          .get("/templates", {
            params: { stationId: organizationId },
          })
          .then((response) => {
            setCustomReportTypes(response.data);
          })
          .catch((err) => {
            console.error(err);
            toast.error(
              "Failed to fetch custom report types. Please try again later.",
            );
          });
      }
    }
  }, [
    userProfile,
    customReportTypes,
    setUserProfile,
    toast,
    setCustomReportTypes,
    organizationId,
  ]);

  useEffect(() => {
    if (userProfile) {
      fetchNews();
    }
  }, [userProfile, fetchNews]);

  return (
    <Box
      display="flex"
      sx={{ minHeight: "100vh", bgcolor: "background.default" }}
    >
      {/* Top AppBar */}
      <TopAppBar
        onMenuClick={handleMobileDrawerToggle}
        onReportTrackerClick={jobMonitorState.toggleDrawer}
        reportTrackerOpen={jobMonitorState.open}
        jobCount={jobMonitorState.jobs.length}
        isPulsating={jobMonitorState.isPulsating}
        getBadgeColor={() => jobMonitorState.badgeColor ?? "default"}
        sidebarOpen={isLgUp ? open : false}
      />

      {/* Sidebar */}
      <Sidebar
        handleSideBarClick={handleSideBarClick}
        open={open}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileDrawerClose}
      />

      {/* Job Monitor */}
      <ToastOffsetSync />
      <JobMonitorView state={jobMonitorState} />

      <Main open={isLgUp ? open : false}>
        <NewsModal
          open={isNewsModalOpen}
          onClose={handleCloseNewsModal}
          newsItems={newsItems}
        />

        {/* Maintenance banner at top of content */}
        <MaintenanceBanner
          items={maintenanceItems}
          onDismiss={dismissMaintenanceItem}
        />

        {/* Main content area */}
        {children}

        <ReportIssueWidget />
      </Main>

      {/* Database Match Panel - visible on all pages except EditSectionPage */}
      {/* {isLgUp && !isEditSectionPage && (
        <ChatPanelContainer
          mode="match"
          sx={{
            position: "sticky",
            top: `calc(64px + ${theme.spacing(2)})`,
            height: `calc(100vh - 64px - 96px - ${theme.spacing(2)})`,
            alignSelf: "flex-start",
            mb: 2,
          }}
        />
      )} */}
    </Box>
  );
};
